///
/// EarthBrowser
///
/// Created by Matt Giger on 10/31/2026
/// Copyright (c) 2026 Geodata Labs
///
/// One instanced draw for every webcam thumbnail chip on screen
/// (webcam_chip.glsl), sampling the shared WebcamThumbAtlas. Same frame
/// protocol as EmberDomeBatch: the cloud node appends chips while it is
/// traversed and the array is uploaded when this node's draw executes.
///

import { NodeBase } from "@stovokor/engine/NodeBase"
import { type RenderActionSet, Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type color } from "@stovokor/math/color"
import { cube } from "@stovokor/math/cube"
import { type matrix } from "@stovokor/math/matrix"
import { sphere } from "@stovokor/math/sphere"
import { type time } from "@stovokor/math/time"
import { vec2 } from "@stovokor/math/vector"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { type Texture } from "@stovokor/shader/Texture"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { WebcamThumbAtlas } from "./WebcamThumbAtlas"

// center.xyz, rect (hw, hh, 0, 0), uv rect, border rgba, params
const cFloatsPerInstance = 19
const cInitialCapacity = 256
const cBoundsKm = 1e7

export const cChipFlagLive = 1
export const cChipFlagStale = 2

class WebcamChipGeometry implements Geometry {
	public corner: VertexAttribute
	public instanceCenter: VertexAttribute
	public instanceRect: VertexAttribute
	public instanceUV: VertexAttribute
	public instanceBorder: VertexAttribute
	public instanceParams: VertexAttribute
	public vertexBuffer: VertexBuffer
	public instanceBuffer: VertexBuffer
	public indexBuffer: IndexBuffer
	public mode: DrawMode = DrawMode.Triangles
	public bounds = new cube(-cBoundsKm, cBoundsKm)
	public boundingSphere = new sphere(cBoundsKm)
	public available: boolean = true
	public instanceCount: number = 0

	constructor(context: StovokorContext) {
		this.corner = new VertexAttribute(context, 2, [-1, -1, 1, -1, 1, 1, -1, 1])
		this.vertexBuffer = new VertexBuffer(context, [this.corner])
		this.indexBuffer = new IndexBuffer(context)
		this.indexBuffer.quad(0, 1, 2, 3)

		this.instanceCenter = new VertexAttribute(context, 3)
		this.instanceRect = new VertexAttribute(context, 4)
		this.instanceUV = new VertexAttribute(context, 4)
		this.instanceBorder = new VertexAttribute(context, 4)
		this.instanceParams = new VertexAttribute(context, 4)
		const instanced = [this.instanceCenter, this.instanceRect, this.instanceUV, this.instanceBorder, this.instanceParams]
		for (const attribute of instanced) {
			attribute.divisor = 1
		}
		this.instanceBuffer = new VertexBuffer(context, instanced)
		this.instanceBuffer.usage = WebGL2RenderingContext.DYNAMIC_DRAW
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.instanceBuffer.destroy()
		this.indexBuffer.destroy()
	}
}

class WebcamChipRenderer extends Renderer {
	public modelView = new UniformDynamic<matrix>()
	public projection = new UniformDynamic<matrix>()
	public viewport = new UniformDynamic<vec2>(new vec2(1, 1))
	public thumbs = new UniformDynamic<Texture>()

	constructor(context: StovokorContext, geometry: Geometry, thumbs: Texture, finalize: () => void) {
		const transparentMaterial: Material = { transparency: true, available: true }
		super(context, new ShaderSourceDefinition("webcam_chip"), geometry, [transparentMaterial])
		this.thumbs.value = thumbs
		this.drawState.cullFace = false
		this.preActions = [finalize]
	}

	protected stateUpdate(state: RenderState): void {
		this.modelView.value = state.modelView
		this.projection.value = state.projection
		this.viewport.value = new vec2(state.width, state.height)
	}
}

export class WebcamChipBatch extends NodeBase {
	public readonly atlas: WebcamThumbAtlas
	private readonly geometry: WebcamChipGeometry
	private data = new Float32Array(cFloatsPerInstance * cInitialCapacity)
	private count = 0
	private frame?: time

	constructor(context: StovokorContext) {
		super()
		this.atlas = new WebcamThumbAtlas(context)
		this.geometry = new WebcamChipGeometry(context)
		this.renderer = new WebcamChipRenderer(context, this.geometry, this.atlas.texture, () => this.finalize())
	}

	public override destroy(): void {
		super.destroy()
		this.atlas.destroy()
	}

	/** Chips appended for the frame being drawn (debugging) */
	public get instanceCount(): number {
		return this.count
	}

	/**
	 * Append one chip at world point (x, y, z) km: `halfW`/`halfH` in logical
	 * px, atlas rect (u0 v0 u1 v1; ignored while `fade` is 0), border color,
	 * flags from cChipFlag*, `hover` 0..1
	 */
	public add(
		state: RenderState, x: number, y: number, z: number, halfW: number, halfH: number,
		u0: number, v0: number, u1: number, v1: number,
		border: color, opacity: number, fade: number, flags: number, hover: number
	): void {
		this.beginFrame(state)
		if ((this.count + 1) * cFloatsPerInstance > this.data.length) {
			const grown = new Float32Array(this.data.length * 2)
			grown.set(this.data)
			this.data = grown
		}
		const d = this.data
		let i = this.count * cFloatsPerInstance
		d[i++] = x
		d[i++] = y
		d[i++] = z
		d[i++] = halfW
		d[i++] = halfH
		d[i++] = 0
		d[i++] = 0
		d[i++] = u0
		d[i++] = v0
		d[i++] = u1
		d[i++] = v1
		d[i++] = border.r
		d[i++] = border.g
		d[i++] = border.b
		d[i++] = border.a
		d[i++] = opacity
		d[i++] = fade
		d[i++] = flags
		d[i] = hover
		this.count += 1
	}

	public override render(state: RenderState): RenderActionSet[] {
		this.beginFrame(state)
		return super.render(state)
	}

	private beginFrame(state: RenderState): void {
		if (this.frame !== state.realTime) {
			this.frame = state.realTime
			this.count = 0
		}
	}

	private finalize(): void {
		this.geometry.instanceCount = this.count
		if (this.count > 0) {
			this.geometry.instanceBuffer.uploadData(this.data)
		}
	}
}

// One batch per GL context
const sharedBatches = new WeakMap<StovokorContext, WebcamChipBatch>()

export function sharedWebcamChipBatch(context: StovokorContext): WebcamChipBatch {
	let batch = sharedBatches.get(context)
	if (!batch) {
		batch = new WebcamChipBatch(context)
		sharedBatches.set(context, batch)
	}
	return batch
}
